/**
 * fetch-api — Helper para chamadas REST diretas ao Express server.
 *
 * Usa API_BASE_URL absoluta (http://localhost:3001 em dev, configurável via
 * VITE_API_URL em prod) e injeta JWT automaticamente do localStorage.
 *
 * Diferente do `expressApiAdapter` (POST com body em /api/{functionName}),
 * este helper suporta GET/POST/PUT/DELETE em paths arbitrários — para
 * endpoints REST modernos como /api/comercial/cards, /api/integrations/:type, etc.
 */

import { handleUnauthorized } from './session-guard';

const API_BASE_URL =
  (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001';

export interface FetchApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

function getAuthToken(): string | null {
  try {
    return localStorage.getItem('auth_token');
  } catch {
    return null;
  }
}

/**
 * Faz fetch contra o Express server. Path deve começar com `/api/...`.
 * Retorna o JSON parseado. Lança Error com mensagem do server em caso de !ok.
 */
export async function fetchApi<T = any>(
  path: string,
  options: FetchApiOptions = {},
): Promise<T> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;

  const res = await fetch(url, {
    ...options,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  // Tenta parsear JSON; se falhar, retorna texto cru
  let json: any;
  const text = await res.text();
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(
      `Resposta não-JSON do servidor (HTTP ${res.status}). Verifique se o server Express está rodando em ${API_BASE_URL}. Início da resposta: ${text.slice(0, 100)}`,
    );
  }

  // Mesma regra do adapter de /api/db: token recusado encerra a sessão em vez
  // de virar um erro solto que a tela transforma em "sem dados".
  if (res.status === 401) handleUnauthorized(path);

  if (!res.ok) {
    const msg =
      json?.error?.message ?? json?.error ?? json?.message ?? `HTTP ${res.status}`;
    const err = new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    (err as any).status = res.status;
    (err as any).body = json;
    throw err;
  }

  return json as T;
}

export const apiUrl = (path: string) =>
  path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
